import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Moon as MoonIcon, Sun, Star, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Nakshatra colors for visual variety
const NAK_COLORS = {
  Ashwini: "#E53935", Bharani: "#D81B60", Krittika: "#FB8C00",
  Rohini: "#43A047", Mrigashira: "#00ACC1", Ardra: "#1E88E5",
  Punarvasu: "#FDD835", Pushya: "#607D8B", Ashlesha: "#7B1FA2",
  Magha: "#FB8C00", "Purva Phalguni": "#EC407A", "Uttara Phalguni": "#43A047",
  Hasta: "#00ACC1", Chitra: "#E53935", Swati: "#1E88E5",
  Vishakha: "#FDD835", Anuradha: "#D81B60", Jyeshtha: "#7B1FA2",
  Mula: "#455A64", "Purva Ashadha": "#FB8C00", "Uttara Ashadha": "#43A047",
  Shravana: "#1E88E5", Dhanishta: "#E53935", Shatabhisha: "#00ACC1",
  "Purva Bhadrapada": "#7B1FA2", "Uttara Bhadrapada": "#607D8B", Revati: "#FDD835",
};

// Tithi names
const TITHIS = [
  "Pratipada", "Dwitiya", "Tritiya", "Chaturthi", "Panchami",
  "Shashthi", "Saptami", "Ashtami", "Navami", "Dashami",
  "Ekadashi", "Dwadashi", "Trayodashi", "Chaturdashi", "Purnima/Amavasya"
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function NakshatraCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    async function fetchCalendarData() {
      setLoading(true);
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/calendar/month?year=${year}&month=${month + 1}`
        );
        if (!response.ok) throw new Error("Failed to fetch calendar data");
        const data = await response.json();
        setCalendarData(data);
      } catch (err) {
        console.error("Calendar fetch error:", err);
        setCalendarData(null);
      } finally {
        setLoading(false);
      }
    }

    fetchCalendarData();
  }, [year, month]);

  const calendarGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const grid = [];

    // Previous month padding
    for (let i = 0; i < firstDay; i++) {
      grid.push({ day: null, isCurrentMonth: false });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = calendarData?.days?.[day] || {};
      grid.push({
        day,
        isCurrentMonth: true,
        nakshatra: dayData.nakshatra,
        tithi: dayData.tithi,
        paksha: dayData.paksha,
        muhurtas: dayData.muhurtas || [],
        transitions: dayData.transitions || [],
        isToday: 
          day === new Date().getDate() && 
          month === new Date().getMonth() && 
          year === new Date().getFullYear(),
      });
    }

    return grid;
  }, [year, month, calendarData]);

  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(new Date().getDate());
  };

  const monthName = currentDate.toLocaleString("default", { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <NavBar />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-violet-400" />
              Nakshatra Calendar
            </h1>
            <p className="text-white/50 mt-1">
              Monthly view of Moon nakshatras, tithis, and auspicious times
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={goToToday}
            className="border-violet-500/50 text-violet-300 hover:bg-violet-500/10"
          >
            Today
          </Button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6 bg-white/5 rounded-xl p-4">
          <Button 
            variant="ghost" 
            onClick={goToPrevMonth}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5 mr-1" /> Previous
          </Button>
          
          <h2 className="text-xl font-semibold text-white">{monthName}</h2>
          
          <Button 
            variant="ghost" 
            onClick={goToNextMonth}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Next <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-white/40 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar cells */}
              {loading ? (
                <div className="grid grid-cols-7 gap-1">
                  {Array(35).fill(null).map((_, i) => (
                    <div key={i} className="aspect-square bg-white/5 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {calendarGrid.map((cell, idx) => (
                    <div
                      key={idx}
                      onClick={() => cell.day && setSelectedDay(cell.day)}
                      className={`
                        aspect-square p-1 rounded-lg cursor-pointer transition-all relative
                        ${cell.isCurrentMonth ? "bg-white/5 hover:bg-white/10" : "bg-transparent"}
                        ${cell.isToday ? "ring-2 ring-violet-500" : ""}
                        ${selectedDay === cell.day ? "bg-violet-500/20 ring-2 ring-violet-400" : ""}
                      `}
                      data-testid={`calendar-day-${cell.day}`}
                    >
                      {cell.day && (
                        <>
                          {/* Day number */}
                          <div className={`text-sm font-medium ${cell.isToday ? "text-violet-300" : "text-white/70"}`}>
                            {cell.day}
                          </div>
                          
                          {/* Nakshatra indicator */}
                          {cell.nakshatra && (
                            <div 
                              className="absolute bottom-1 left-1 right-1 h-1.5 rounded-full opacity-60"
                              style={{ backgroundColor: NAK_COLORS[cell.nakshatra] || "#7B1FA2" }}
                              title={cell.nakshatra}
                            />
                          )}
                          
                          {/* Special day indicators */}
                          {cell.tithi?.includes("Purnima") && (
                            <MoonIcon className="absolute top-1 right-1 w-3 h-3 text-amber-400" />
                          )}
                          {cell.tithi?.includes("Amavasya") && (
                            <MoonIcon className="absolute top-1 right-1 w-3 h-3 text-gray-600" />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="text-white/40">Legend:</span>
              <span className="flex items-center gap-1">
                <MoonIcon className="w-3 h-3 text-amber-400" /> Full Moon
              </span>
              <span className="flex items-center gap-1">
                <MoonIcon className="w-3 h-3 text-gray-600" /> New Moon
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-1.5 rounded-full bg-violet-500" /> Nakshatra
              </span>
            </div>
          </div>

          {/* Day Details Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 sticky top-24">
              {selectedDay ? (
                <DayDetails 
                  day={selectedDay} 
                  month={month} 
                  year={year}
                  data={calendarData?.days?.[selectedDay]}
                />
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40">Select a day to see details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function DayDetails({ day, month, year, data }) {
  const dateStr = new Date(year, month, day).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  if (!data) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">{dateStr}</h3>
        <p className="text-white/40 text-sm">No data available for this day</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="day-details">
      <h3 className="text-lg font-semibold text-white">{dateStr}</h3>

      {/* Nakshatra */}
      {data.nakshatra && (
        <div className="p-3 rounded-lg bg-black/30">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-white/40 uppercase">Moon Nakshatra</span>
          </div>
          <Link 
            to={`/nakshatra/${data.nakshatra_slug || data.nakshatra.toLowerCase().replace(/\s+/g, '-')}`}
            className="text-white font-medium hover:text-violet-300 transition-colors"
          >
            {data.nakshatra}
          </Link>
          {data.pada && (
            <span className="text-white/50 text-sm ml-2">Pada {data.pada}</span>
          )}
        </div>
      )}

      {/* Tithi */}
      {data.tithi && (
        <div className="p-3 rounded-lg bg-black/30">
          <div className="flex items-center gap-2 mb-1">
            <MoonIcon className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-white/40 uppercase">Tithi (Lunar Day)</span>
          </div>
          <div className="text-white font-medium">{data.tithi}</div>
          {data.paksha && (
            <span className="text-white/50 text-sm">{data.paksha} Paksha</span>
          )}
        </div>
      )}

      {/* Nakshatra Transitions */}
      {data.transitions?.length > 0 && (
        <div className="p-3 rounded-lg bg-black/30">
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-white/40 uppercase">Transitions</span>
          </div>
          <div className="space-y-2">
            {data.transitions.map((t, i) => (
              <div key={i} className="text-sm">
                <span className="text-white/60">{t.time}</span>
                <span className="text-white/40 mx-2">→</span>
                <span className="text-white">{t.to_nakshatra}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Muhurtas */}
      {data.muhurtas?.length > 0 && (
        <div className="p-3 rounded-lg bg-black/30">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-white/40 uppercase">Auspicious Times</span>
          </div>
          <div className="space-y-2">
            {data.muhurtas.map((m, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-emerald-300">{m.name}</span>
                <span className="text-white/50">{m.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Note about general guidance */}
      <p className="text-xs text-white/30 pt-2">
        Times shown are approximate and based on general calculations. 
        For precise muhurtas, consult a local panchang.
      </p>
    </div>
  );
}
